/**
 * PromptWorkbench — Phase 3.C
 *
 * Side-by-side diff editor for The Kitchen's two prompt families:
 *   • Picker prompts: notebooks/ai_pickers/prompts/<sport>/*.md
 *   • Grader prompts: parlay-scorer-worker/src/worker.js PROMPT_*
 *
 * console-reporter publishes both into R2:_console/prompts/ every
 * 60s. Worker exposes /api/prompts (index) and /api/prompt?path=
 * (raw text). The workbench shows current vs draft side-by-side
 * (CodeMirror 6 + @codemirror/merge), with token-cost preview.
 *
 * Save flow (this phase):
 *   - Drafts persist to localStorage
 *   - "Copy" button copies the draft text — paste into your editor
 *     of choice, commit normally
 *   - Phase 3.E will add a /test-score live calibration preview
 *   - Phase 3.F will add direct save-back via worker write
 */
import { html, type TemplateResult } from "lit-html";
import { EditorState, Compartment } from "@codemirror/state";
import { EditorView, lineNumbers, highlightActiveLine } from "@codemirror/view";
import { markdown } from "@codemirror/lang-markdown";
import { unifiedMergeView } from "@codemirror/merge";
import { oneDark } from "@codemirror/theme-one-dark";
import { store } from "../data/store";
import { api } from "../data/api";
import type { PromptIndex, PromptDescriptor } from "../data/types";
import { renderExplainButton } from "./ExplainButton";

const DRAFT_KEY_PFX = "kitchen-console:prompt-draft:";

interface ViewState {
  selectedPath:  string | null;
  loading:       boolean;
  current:       string;             // current published text
  draft:         string;             // local edit
  error:         Error | null;
  view:          EditorView | null;
  loadedPath:    string | null;      // what's currently in the editor
}

const view: ViewState = {
  selectedPath: null,
  loading:      false,
  current:      "",
  draft:        "",
  error:        null,
  view:         null,
  loadedPath:   null,
};

const themeCompartment = new Compartment();

// Analyze-diff state — tracks Claude's streaming response
interface AnalyzeState {
  running: boolean;
  text:    string;
  error:   Error | null;
  abort:   AbortController | null;
}
const analyzeState: AnalyzeState = {
  running: false,
  text:    "",
  error:   null,
  abort:   null,
};

async function analyzeDiff(p: PromptDescriptor, onChange: () => void): Promise<void> {
  if (analyzeState.running) return;
  analyzeState.abort?.abort();
  const ac = new AbortController();
  analyzeState.abort   = ac;
  analyzeState.running = true;
  analyzeState.text    = "";
  analyzeState.error   = null;
  onChange();

  try {
    const r = await fetch("/api/analyze-prompt-diff", {
      method:  "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body:    JSON.stringify({
        prompt_name: p.name,
        current:     view.current,
        draft:       view.draft,
      }),
      signal:  ac.signal,
      redirect: "error",
    });
    if (!r.ok) {
      const errBody = await r.text().catch(() => "");
      throw new Error(`/api/analyze-prompt-diff → ${r.status}: ${errBody.slice(0, 200)}`);
    }
    if (!r.body) throw new Error("no_response_body");

    const reader  = r.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const dataLine = frame.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        try {
          const obj = JSON.parse(dataLine.slice(5).trim());
          if (obj.type === "content_block_delta" && obj.delta?.type === "text_delta") {
            analyzeState.text += obj.delta.text as string;
            onChange();
          }
        } catch { /* ignore */ }
      }
    }
  } catch (e) {
    if ((e as Error).name === "AbortError") return;
    analyzeState.error = e instanceof Error ? e : new Error(String(e));
  } finally {
    analyzeState.running = false;
    analyzeState.abort   = null;
    onChange();
  }
}

function analyzeResultsPanel(): import("lit-html").TemplateResult {
  return html`
    <section class="pw-analyze">
      <header class="pw-analyze__head">
        <span class="pw-analyze__icon">✨</span>
        <span class="pw-analyze__title">Diff analysis</span>
        <span class="pw-analyze__spacer"></span>
        <span class="pw-analyze__model">claude-sonnet-4-6 · streaming</span>
      </header>
      ${analyzeState.error ? html`
        <div class="pw-analyze__error">${analyzeState.error.message}</div>
      ` : ""}
      <div class="pw-analyze__body">
        ${analyzeState.text
          ? renderAnalyzeMarkdown(analyzeState.text)
          : analyzeState.running
            ? html`<span class="pw-analyze__shimmer">analyzing draft…</span>`
            : ""}
        ${analyzeState.running ? html`<span class="pw-analyze__caret"></span>` : ""}
      </div>
    </section>
  `;
}

function renderAnalyzeMarkdown(text: string): import("lit-html").TemplateResult[] {
  return text.split("\n").map((line) => {
    const segs: import("lit-html").TemplateResult[] = [];
    const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) segs.push(html`${line.slice(last, m.index)}`);
      const tok = m[0];
      if (tok.startsWith("**")) segs.push(html`<strong>${tok.slice(2, -2)}</strong>`);
      else                       segs.push(html`<code>${tok.slice(1, -1)}</code>`);
      last = re.lastIndex;
    }
    if (last < line.length) segs.push(html`${line.slice(last)}`);
    return html`<div class="pw-analyze__line">${segs}</div>`;
  });
}

// =============================================================
// Public render
// =============================================================
export function renderPromptWorkbench(onChange: () => void): TemplateResult {
  const e = store.get<PromptIndex>("promptsIndex");
  if (e.error) return errorView(e.error);
  if (!e.data && e.loading) return skeletonView();
  if (!e.data) return emptyView();

  const idx = e.data;
  if ((idx as any).error === "prompts_not_synced") {
    return notSyncedView();
  }

  // First render: auto-select the first picker prompt
  if (!view.selectedPath && idx.prompts.length > 0) {
    view.selectedPath = idx.prompts[0]!.path;
  }

  const picker = idx.prompts.filter((p) => p.kind === "picker");
  const grader = idx.prompts.filter((p) => p.kind === "grader");
  const selected = view.selectedPath
    ? idx.prompts.find((p) => p.path === view.selectedPath) ?? null
    : null;

  // Lazy-load body when selection changes
  if (selected && view.loadedPath !== selected.path) {
    void loadPrompt(selected.path, onChange);
  }

  return html`
    <div class="pw">
      ${headerCard(idx, selected)}
      <div class="pw__layout">
        ${promptList(picker, grader, onChange)}
        ${editorPane(selected, onChange)}
      </div>
    </div>
  `;
}

// =============================================================
// Header card — meta + actions
// =============================================================
function headerCard(idx: PromptIndex, selected: PromptDescriptor | null): TemplateResult {
  const ageMs = Date.parse(idx.generated_at) ? Date.now() - Date.parse(idx.generated_at) : 0;
  const ageStr = ageMs > 0
    ? `${Math.floor(ageMs / 1000)}s ago`
    : "—";

  return html`
    <section class="panel pw-header">
      <div class="pw-header__row">
        <div class="pw-header__metric">
          <span class="pw-header__label">grader version</span>
          <span class="pw-header__value">${idx.grader_version ?? "—"}</span>
        </div>
        <div class="pw-header__metric">
          <span class="pw-header__label">prompts</span>
          <span class="pw-header__value">${idx.prompts.length}</span>
          <span class="pw-header__sub">
            ${idx.prompts.filter((p) => p.kind === "picker").length} picker ·
            ${idx.prompts.filter((p) => p.kind === "grader").length} grader
          </span>
        </div>
        <div class="pw-header__metric">
          <span class="pw-header__label">last sync</span>
          <span class="pw-header__value">${ageStr}</span>
        </div>
        <span class="pw-header__spacer"></span>
        ${selected ? renderExplainButton({
          label: "Explain this prompt",
          context: () => buildPromptExplainContext(selected, view.current),
        }) : ""}
      </div>
    </section>
  `;
}

// =============================================================
// Prompt list — left column
// =============================================================
function promptList(
  picker: PromptDescriptor[],
  grader: PromptDescriptor[],
  onChange: () => void,
): TemplateResult {
  // Group picker by sport
  const bySport: Record<string, PromptDescriptor[]> = {};
  for (const p of picker) {
    bySport[p.sport] = bySport[p.sport] ?? [];
    bySport[p.sport]!.push(p);
  }
  const graderBySport: Record<string, PromptDescriptor[]> = {};
  for (const p of grader) {
    graderBySport[p.sport] = graderBySport[p.sport] ?? [];
    graderBySport[p.sport]!.push(p);
  }

  return html`
    <aside class="pw-list panel">
      <h4>Picker prompts</h4>
      ${Object.keys(bySport).sort().map((sport) => html`
        <details class="pw-list__group" open>
          <summary>
            <span class="pw-list__sport">${sport}</span>
            <span class="pw-list__count">${bySport[sport]!.length}</span>
          </summary>
          ${bySport[sport]!.map((p) => promptRow(p, onChange))}
        </details>
      `)}

      <h4 style="margin-top:18px;">Grader prompts</h4>
      ${Object.keys(graderBySport).sort().map((sport) => html`
        <details class="pw-list__group" open>
          <summary>
            <span class="pw-list__sport">${sport}</span>
            <span class="pw-list__count">${graderBySport[sport]!.length}</span>
          </summary>
          ${graderBySport[sport]!.map((p) => promptRow(p, onChange))}
        </details>
      `)}
    </aside>
  `;
}

function promptRow(p: PromptDescriptor, onChange: () => void): TemplateResult {
  const isSelected = view.selectedPath === p.path;
  const draftKey = DRAFT_KEY_PFX + p.path;
  const hasDraft = (() => {
    try {
      const v = localStorage.getItem(draftKey);
      return v != null && v !== "";
    } catch { return false; }
  })();
  return html`
    <button
      class="pw-list__row ${isSelected ? "is-selected" : ""}"
      @click=${() => {
        view.selectedPath = p.path;
        onChange();
      }}
    >
      <span class="pw-list__name">${p.name}</span>
      <span class="pw-list__meta">${p.lines}L · ${(p.bytes / 1024).toFixed(1)}KB</span>
      ${hasDraft ? html`<span class="pw-list__draft-pill">draft</span>` : ""}
    </button>
  `;
}

// =============================================================
// Editor pane — diff editor
// =============================================================
function editorPane(p: PromptDescriptor | null, onChange: () => void): TemplateResult {
  if (!p) {
    return html`
      <main class="pw-editor panel">
        <div class="pw-editor__empty">Pick a prompt from the list to start editing.</div>
      </main>
    `;
  }

  const isModified = view.draft !== view.current && view.loadedPath === p.path;
  const draftKey = DRAFT_KEY_PFX + p.path;
  const draftLines = view.draft.split("\n").length;
  const draftBytes = new Blob([view.draft]).size;
  const draftDelta = view.draft.length - view.current.length;

  // Token cost estimate (rough — 4 chars ≈ 1 token, +20% safety)
  const draftTokensEst = Math.ceil(view.draft.length / 4 * 1.2);

  return html`
    <main class="pw-editor panel">
      <header class="pw-editor__head">
        <div class="pw-editor__heading">
          <code class="pw-editor__path">${p.path}</code>
          <span class="pw-editor__meta">
            ${draftLines}L · ${(draftBytes / 1024).toFixed(1)}KB
            ${draftDelta !== 0 ? html`
              <span class="${draftDelta > 0 ? "is-pos" : "is-neg"}"
                    style="margin-left:8px;">
                ${draftDelta > 0 ? "+" : ""}${draftDelta} bytes
              </span>
            ` : ""}
            <span style="margin-left:8px;color:var(--text-quaternary);">
              · ~${draftTokensEst.toLocaleString()} tok
            </span>
          </span>
        </div>
        <div class="pw-editor__actions">
          ${isModified ? html`
            <button class="pw-btn pw-btn--secondary"
                    @click=${() => {
                      view.draft = view.current;
                      try { localStorage.removeItem(draftKey); } catch {}
                      remountEditor();
                      onChange();
                    }}
                    title="Discard draft, restore published">
              revert
            </button>
            <button class="pw-btn pw-btn--primary"
                    @click=${() => analyzeDiff(p, onChange)}
                    ?disabled=${analyzeState.running}
                    title="Claude reads current vs draft, narrates what scoring behaviors change">
              ✨ ${analyzeState.running ? "analyzing…" : "analyze diff"}
            </button>
            <button class="pw-btn pw-btn--primary"
                    @click=${() => copyDraftAndExplain(p)}>
              copy draft
            </button>
          ` : ""}
        </div>
      </header>

      ${analyzeState.running || analyzeState.text || analyzeState.error
        ? analyzeResultsPanel()
        : ""}

      <div id="pw-merge-host" class="pw-editor__merge"></div>

      ${isModified ? html`
        <footer class="pw-editor__foot">
          <strong>Save flow:</strong>
          ${p.kind === "picker" ? html`
            Edit → "copy draft" → paste into
            <code>notebooks/ai_pickers/prompts/${p.sport}/${p.name}.md</code>,
            commit. Next picker run uses it.
          ` : html`
            Edit → "copy draft" → paste into the matching
            <code>${p.name}</code> const in
            <code>parlay-scorer-worker/src/worker.js</code>, bump
            <code>PROMPT_VERSION</code>, then
            <code>wrangler deploy</code>.
          `}
          <span style="color:var(--text-quaternary);margin-left:6px;">
            Phase 3.E adds live calibration preview; Phase 3.F adds direct save-back.
          </span>
        </footer>
      ` : ""}
    </main>
  `;
}

// =============================================================
// Editor lifecycle
// =============================================================
async function loadPrompt(path: string, onChange: () => void): Promise<void> {
  if (view.loading) return;
  view.loading = true;
  view.error = null;
  view.loadedPath = path;
  try {
    const text = await api.promptBody(path);
    view.current = text;
    // Pull draft from localStorage if present
    let draft = text;
    try {
      const d = localStorage.getItem(DRAFT_KEY_PFX + path);
      if (d != null && d !== "") draft = d;
    } catch { /* noop */ }
    view.draft = draft;
    view.loading = false;
    onChange();
    queueMicrotask(remountEditor);
  } catch (e) {
    view.loading = false;
    view.error = e instanceof Error ? e : new Error(String(e));
    onChange();
  }
}

function remountEditor(): void {
  const host = document.getElementById("pw-merge-host");
  if (!host) return;
  if (view.view) { view.view.destroy(); view.view = null; }

  // CodeMirror 6 unified merge view: shows the draft as the editable
  // doc, with the original (current) shown inline as accept/reject
  // diffs.
  const editor = new EditorView({
    parent: host,
    state: EditorState.create({
      doc: view.draft,
      extensions: [
        lineNumbers(),
        highlightActiveLine(),
        markdown(),
        themeCompartment.of(oneDark),
        EditorView.theme({
          "&":             { height: "100%", fontSize: "13px" },
          ".cm-scroller":  { fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace" },
          ".cm-content":   { padding: "12px 0" },
          ".cm-gutters":   { background: "transparent", border: "none" },
        }),
        unifiedMergeView({
          original: view.current,
          mergeControls: false,
        }),
        EditorView.updateListener.of((u) => {
          if (u.docChanged) {
            view.draft = u.state.doc.toString();
            // Persist after a short debounce
            scheduleDraftSave();
          }
        }),
      ],
    }),
  });
  view.view = editor;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleDraftSave(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    if (!view.loadedPath) return;
    try {
      if (view.draft === view.current) {
        localStorage.removeItem(DRAFT_KEY_PFX + view.loadedPath);
      } else {
        localStorage.setItem(DRAFT_KEY_PFX + view.loadedPath, view.draft);
      }
    } catch { /* noop */ }
  }, 400);
}

function copyDraftAndExplain(p: PromptDescriptor): void {
  navigator.clipboard?.writeText(view.draft).then(() => {
    flashToast(`copied ${p.name} draft`);
  }).catch(() => {
    flashToast("clipboard write failed", true);
  });
}

function flashToast(msg: string, error = false): void {
  const t = document.createElement("div");
  t.className = "pw-toast";
  t.style.background = error
    ? "rgba(255,69,58,0.16)"
    : "rgba(48,209,88,0.16)";
  t.style.borderColor = error
    ? "rgba(255,69,58,0.40)"
    : "rgba(48,209,88,0.40)";
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => {
    t.style.opacity = "0";
    setTimeout(() => t.remove(), 200);
  }, 1800);
}

// =============================================================
// Helpers
// =============================================================
function buildPromptExplainContext(p: PromptDescriptor, text: string): string {
  return [
    `Prompt: ${p.path} (${p.kind} / ${p.sport} / ${p.name})`,
    `Lines: ${p.lines}, bytes: ${p.bytes}`,
    p.version ? `Grader version: ${p.version}` : "",
    `Last modified: ${p.mtime}`,
    "",
    "Content:",
    text,
  ].filter(Boolean).join("\n");
}

// =============================================================
// State views
// =============================================================
function skeletonView(): TemplateResult {
  return html`
    <div class="pw">
      <section class="panel" style="height:80px;"></section>
      <div class="pw__layout">
        <aside class="pw-list panel" style="min-height:480px;">
          ${Array.from({ length: 8 }, () => html`
            <div class="skeleton" style="height:24px;margin-bottom:6px;"></div>
          `)}
        </aside>
        <main class="pw-editor panel" style="min-height:480px;"></main>
      </div>
    </div>
  `;
}

function emptyView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:520px;margin:0 auto;">
        No prompt index yet. Make sure
        <code style="color:var(--accent-blue-light);">com.yurysankov.console-reporter</code>
        is loaded — it publishes prompts to R2 every 60s.
      </div>
    </div>
  `;
}

function notSyncedView(): TemplateResult {
  return html`
    <div class="panel" style="padding:48px;text-align:center;">
      <div style="color:var(--text-tertiary);font-size:14px;line-height:1.5;
                   max-width:520px;margin:0 auto;">
        <p style="font-size:15px;color:var(--text-primary);margin:0 0 8px;">
          Prompt sync hasn't run yet
        </p>
        <p style="margin:0;">
          The console-reporter publishes prompts every 60s. Wait one
          minute and reload — or trigger it manually:
        </p>
        <pre style="margin:14px auto 0;text-align:left;
                     font-size:11px;background:var(--bg-glass-thin);
                     border:1px solid var(--border-1);
                     border-radius:8px;padding:10px;
                     max-width:480px;color:var(--accent-blue-light);">
~/.hermes/hermes-agent/venv/bin/python3 \\
  ~/Code/sports-betting-research/notebooks/console_reporter.py
        </pre>
      </div>
    </div>
  `;
}

function errorView(err: Error): TemplateResult {
  return html`
    <div class="panel" style="padding:32px;border-color:rgba(255,69,58,0.32);">
      <div class="panel__title">
        <span>Prompt Workbench</span>
        <span class="phase-stub-badge"
              style="background:rgba(255,69,58,0.16);
                      color:var(--accent-red-light);
                      border-color:rgba(255,69,58,0.32);">
          fetch error
        </span>
      </div>
      <div style="color:var(--accent-red-light);font-size:12px;
                   line-height:1.5;font-family:monospace;
                   word-break:break-all;white-space:pre-wrap;
                   margin-top:8px;">
        ${err.message}
      </div>
    </div>
  `;
}
